import { z } from 'zod';

/** Timestamps cross JSON serialization as ISO strings. */
export const Dateish = z.union([z.date(), z.string()]);

export const PaginationQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

/** Postgres int4 upper bound — ids beyond this can't exist and would overflow. */
export const PG_INT_MAX = 2_147_483_647;

/**
 * Coerção ÚNICA de id int4 para params/queries: int, positivo, teto
 * PG_INT_MAX. Um id acima do int4 não pode existir — sem o teto ele atravessa
 * a validação e vira 500 do Postgres (inclusive em rota não autenticada).
 */
export const IdCoerce = z.coerce.number().int().positive().max(PG_INT_MAX);

export const IdParamSchema = z.object({ id: IdCoerce });
export const TicketNumberParamSchema = z.object({ ticketNumber: IdCoerce });

/**
 * Boolean query param: z.coerce.boolean() is Boolean(input), so '?x=false'
 * would coerce to TRUE. This parses the string forms correctly.
 */
export const BooleanQuery = z
  .enum(['true', 'false', '1', '0'])
  .transform((v) => v === 'true' || v === '1');

/**
 * E-mail OPCIONAL de contato (solicitante do ticket, hoje; qualquer campo
 * "e-mail se houver", amanhã).
 *
 * Existe porque `z.string().nullish()` puro aceitava QUALQUER texto e o
 * endereço malformado seguia direto para o SES, onde vira bounce — e bounce
 * de endereço inexistente é cobrado contra a reputação da CONTA inteira, não
 * do tenant que digitou errado. Validar aqui é a barreira mais barata.
 *
 * Ausente, `null` e string VAZIA continuam aceitos de propósito: o campo é
 * opcional, o SPA manda `''` ao limpar o campo e o PATCH parcial precisa poder
 * apagar o endereço. `''` normaliza para `null` — limpar o campo passa a
 * limpar de verdade, em vez de gravar um endereço vazio que nunca entrega.
 */
export const OptionalEmail = z
  .string()
  .trim()
  // 320 = 64 (local) + 1 (@) + 255 (domínio), o teto do RFC 3696
  .pipe(z.union([z.literal(''), z.email().max(320)]))
  .transform((v) => (v === '' ? null : v))
  .nullish();

export const ErrorResponseSchema = z.object({
  error: z.string(),
  code: z.string().optional(),
});
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;

/** Comma-separated int list in query strings: "1,2,3" -> [1,2,3] */
export const intList = z
  .string()
  .transform((s) =>
    s
      .split(',')
      .map((x) => Number(x.trim()))
      .filter((n) => Number.isInteger(n) && n > 0)
  )
  .pipe(z.array(z.number().int().positive()).max(15));
